import { useState } from "react"
import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { AccountLifecycleBadge } from "@/components/AccountLifecycleBadge"
import { LicenseStatusBadge } from "@/components/LicenseStatusBadge"
import { PageHeader } from "@/components/PageHeader"
import { SubscriptionStatusBadge } from "@/components/SubscriptionStatusBadge"
import { Badge } from "@/components/ui/badge"
import { DataTable, DataRow } from "@/components/ui/data-table"
import { SubNav } from "@/components/ui/sub-nav"
import { formatCents } from "@/lib/fetch-json"

interface Member {
  id: number
  email: string
  role: string
}

interface SubscriptionRow {
  id: number
  stripe_subscription_id: string
  status: string
  quantity: number
  pending_quantity: number | null
  cancel_at_period_end: boolean
  current_period_end: string | null
}

interface RedemptionRow {
  id: number
  deal_name: string
  status: string
  amount_cents: number
  completed_at: string | null
}

interface SeatRow {
  id: number
  source: "subscription" | "lifetime"
  released: boolean
  assignee_email: string | null
  license: {
    id: number
    masked_key: string
    status: string
    device_count: number
  } | null
}

interface PaymentRow {
  id: number
  invoice_number: string
  description: string
  amount_cents: number
  paid_at: string
}

interface AccountDetail {
  id: number
  name: string
  lifecycle_status: string
  stripe_customer_id: string | null
  created_at: string
  billing: {
    billing_name: string | null
    billing_address: string | null
    billing_extra: string | null
  }
  members: Member[]
  subscriptions: SubscriptionRow[]
  redemptions: RedemptionRow[]
  seats: SeatRow[]
  payments: PaymentRow[]
}

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "members", label: "Members" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "redemptions", label: "Redemptions" },
  { key: "seats", label: "Seats & licenses" },
  { key: "payments", label: "Payments" },
]

export default function AdminAccountShow({ account }: { account: AccountDetail }) {
  const [tab, setTab] = useState("overview")

  return (
    <>
      <Head title={account.name}>
        <meta name="description" content={`Admin detail for account ${account.name}.`} />
        <meta property="og:title" content={account.name} />
        <meta property="og:description" content={`Admin detail for account ${account.name}.`} />
      </Head>
      <AdminShell>
        <PageHeader
          title={account.name}
          description={`Account #${account.id}, created ${formatDate(account.created_at)}.`}
          actions={
            <>
              <AccountLifecycleBadge status={account.lifecycle_status} />
              <Link href="/admin/accounts" className="text-sm text-ink-muted no-underline hover:text-ink-display">
                Back to accounts
              </Link>
            </>
          }
          tabs={<SubNav items={TABS} active={tab} onChange={setTab} ariaLabel="Account sections" />}
        />

        <section className={tab === "overview" ? "mt-8" : "hidden"}>
          <DataTable className="mt-4">
            <DataRow title="Owner">
              {account.members.find((m) => m.role === "owner")?.email ?? "—"}
            </DataRow>
            <DataRow title="Stripe customer">
              {account.stripe_customer_id ? (
                <code className="font-mono text-sm">{account.stripe_customer_id}</code>
              ) : (
                "—"
              )}
            </DataRow>
            <DataRow title="Invoice name">{account.billing.billing_name ?? "—"}</DataRow>
            <DataRow title="Invoice address">
              {account.billing.billing_address ? (
                <span className="whitespace-pre-line">{account.billing.billing_address}</span>
              ) : (
                "—"
              )}
            </DataRow>
            <DataRow title="Invoice extra">
              {account.billing.billing_extra ? (
                <span className="whitespace-pre-line">{account.billing.billing_extra}</span>
              ) : (
                "—"
              )}
            </DataRow>
          </DataTable>
        </section>

        <section className={tab === "members" ? "mt-8" : "hidden"}>
          <ul className="mt-4 divide-y divide-hairline rounded-lg border border-hairline">
            {account.members.map((member) => (
              <li key={member.id} className="flex items-center justify-between gap-2 px-4 py-3">
                <span className="text-sm text-ink-body">{member.email}</span>
                <Badge tone={member.role === "owner" ? "accent" : "neutral"}>
                  {member.role === "owner" ? "Owner" : "Member"}
                </Badge>
              </li>
            ))}
          </ul>
        </section>

        <section className={tab === "subscriptions" ? "mt-8" : "hidden"}>
          {account.subscriptions.length === 0 ? (
            <p className="mt-4 text-sm text-ink-muted">No subscriptions.</p>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline bg-surface text-left">
                    <th className="px-4 py-3 font-medium text-ink-muted">Stripe ID</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                    <th className="px-4 py-3 text-right font-medium text-ink-muted">Seats</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Period ends</th>
                  </tr>
                </thead>
                <tbody>
                  {account.subscriptions.map((sub) => (
                    <tr key={sub.id} className="border-b border-hairline last:border-0">
                      <td className="px-4 py-3">
                        <code className="font-mono text-xs">{sub.stripe_subscription_id}</code>
                      </td>
                      <td className="px-4 py-3">
                        <SubscriptionStatusBadge status={sub.status} />
                        {sub.cancel_at_period_end && (
                          <span className="ml-2 text-xs text-ink-muted">cancels at period end</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {sub.quantity}
                        {sub.pending_quantity != null && (
                          <span className="text-xs text-ink-muted"> → {sub.pending_quantity}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {sub.current_period_end ? formatDate(sub.current_period_end) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className={tab === "redemptions" ? "mt-8" : "hidden"}>
          {account.redemptions.length === 0 ? (
            <p className="mt-4 text-sm text-ink-muted">No redemptions.</p>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline bg-surface text-left">
                    <th className="px-4 py-3 font-medium text-ink-muted">Deal</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                    <th className="px-4 py-3 text-right font-medium text-ink-muted">Amount</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {account.redemptions.map((redemption) => (
                    <tr key={redemption.id} className="border-b border-hairline last:border-0">
                      <td className="px-4 py-3">{redemption.deal_name}</td>
                      <td className="px-4 py-3">
                        <RedemptionStatusBadge status={redemption.status} />
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {formatCents(redemption.amount_cents)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {redemption.completed_at ? formatDate(redemption.completed_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className={tab === "seats" ? "mt-8" : "hidden"}>
          {account.seats.length === 0 ? (
            <p className="mt-4 text-sm text-ink-muted">No seats.</p>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline bg-surface text-left">
                    <th className="px-4 py-3 font-medium text-ink-muted">License</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Source</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Assigned to</th>
                    <th className="px-4 py-3 text-right font-medium text-ink-muted">Devices</th>
                  </tr>
                </thead>
                <tbody>
                  {account.seats.map((seat) => (
                    <tr key={seat.id} className="border-b border-hairline last:border-0">
                      <td className="px-4 py-3">
                        {seat.license ? (
                          <Link href={`/admin/licenses/${seat.license.id}`} className="font-mono text-xs">
                            {seat.license.masked_key}
                          </Link>
                        ) : (
                          <span className="text-ink-muted">no license</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {seat.license ? (
                          <LicenseStatusBadge status={seat.license.status} />
                        ) : seat.released ? (
                          <Badge tone="muted">Released</Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {seat.source === "lifetime" ? "Lifetime" : "Subscription"}
                      </td>
                      <td className="px-4 py-3">{seat.assignee_email ?? "Unassigned"}</td>
                      <td className="px-4 py-3 text-right">{seat.license?.device_count ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className={tab === "payments" ? "mt-8" : "hidden"}>
          {account.payments.length === 0 ? (
            <p className="mt-4 text-sm text-ink-muted">No payments.</p>
          ) : (
            <div className="mt-4 overflow-x-auto rounded-lg border border-hairline">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline bg-surface text-left">
                    <th className="px-4 py-3 font-medium text-ink-muted">Invoice</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Description</th>
                    <th className="px-4 py-3 text-right font-medium text-ink-muted">Amount</th>
                    <th className="px-4 py-3 font-medium text-ink-muted">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {account.payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-hairline last:border-0">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <code className="font-mono text-xs">{payment.invoice_number}</code>
                      </td>
                      <td className="px-4 py-3">{payment.description}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {formatCents(payment.amount_cents)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(payment.paid_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </AdminShell>
    </>
  )
}

function RedemptionStatusBadge({ status }: { status: string }) {
  const tone =
    status === "completed" ? ("accent" as const) : status === "pending" ? ("signal" as const) : ("muted" as const)
  return <Badge tone={tone}>{status}</Badge>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
